import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import GroundStateBadge from './GroundStateBadge';
import { MapContainer, TileLayer, useMap, CircleMarker, Tooltip, Polyline, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './Predictions.css';
import { parseServerTime } from '../utils/serverTime';
import { errorToBandClass } from '../utils/errorBands';
import { getAccessToken } from '../utils/auth';
import { getDepartureHistory, getAirportFeatures, getAircraftOnGround } from '../utils/httpApi';
import { rowKey, effectiveState, departureOrder, remainingTaxiMinutes, formatRemainingMmSs, firstErrorMinutes, groundCounts, groupDepartureRows } from '../utils/departureRows';
import useDepartureRows from '../hooks/useDepartureRows';
import GroundCounters from './GroundCounters';
import { getTileLayerConfig } from '../utils/mapStyles';
import { useSettings } from '../contexts/SettingsContext';
import { FlagIcon, AirlineLogo, AircraftTypeIcon } from './AircraftTableComponents';
import { getCountryFromHex } from '../utils/icaoCountryMap';
import AircraftMarker from './AircraftMarker';
import { isGroundVehicle } from '../utils/groundVehicle';
import { departureTrackQuery } from '../utils/landingTrack';
import useLandingTrack from '../hooks/useLandingTrack';
import { LandingTrackLine, LandingTrackPopup } from './LandingTrack';

// Local copies of helpers also used by Predictions.js — kept here so the
// component is self-contained and can be lifted later.
//
// `bounds` is a tight box around the actual airport perimeter (runways +
// aprons + taxiways), not the airport reference point. The default 'airport'
// auto-zoom expands this by 500 m so the surface is fully visible without
// over-zooming. Computed from airport_feature runway extents (V001 import).
const SUPPORTED_AIRPORTS = [
  {
    icao: 'EIDW',
    name: 'Dublin (EIDW)',
    lat: 53.4213,
    lon: -6.2701,
    tz: 'Europe/Dublin',
    // EIDW airport perimeter — derived from runway 10R/28L (53.4203-53.4224
    // east-west) + 10L/28R (53.4352-53.4372 north-south) + 16/34
    // (53.4199-53.4370). Box covers all hard surfaces.
    bounds: { minLat: 53.4199, maxLat: 53.4373, minLon: -6.2900, maxLon: -6.2382 },
  },
];

function airportTzName(icao) {
  return SUPPORTED_AIRPORTS.find(a => a.icao === icao)?.tz || 'UTC';
}

function formatLocalHourMin(date, airportIcao) {
  if (!date) return '-';
  try {
    const d = (date instanceof Date) ? date : parseServerTime(date);
    if (!d) return '-';
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit', minute: '2-digit', hour12: false,
      timeZone: airportTzName(airportIcao),
    }).format(d);
  } catch { return '-'; }
}

function formatDelta(min) {
  if (min == null) return '-';
  const sign = min > 0 ? '+' : '';
  return `${sign}${min.toFixed(1)}m`;
}

// Read a theme.css variable at runtime — used for Leaflet pathOptions which
// require literal colour strings rather than CSS class names.
function cssVar(name) {
  if (typeof window === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

// Resolve the band's colour at runtime via the CSS variables — Leaflet's
// pathOptions need a literal colour string, so we can't just hand it a class.
function errorToCssVar(errorMin) {
  const cls = errorToBandClass(errorMin);
  const map = {
    'err-good': '--status-connected',
    'err-warn': '--status-warning',
    'err-bad':  '--accent-primary',
    'err-very-bad': '--accent-primary-hover',
  };
  return cssVar(map[cls]);
}

// Auto-zoom variants. 'airport' is the new default for departures —
// fits a fixed ~5 km box around the airport reference point so the
// map shows the surface clearly, no marker-dependent scaling.
function DeparturesAutoZoom({ mode, airport, markers, expandedKey }) {
  const map = useMap();
  // markers update once per second (liveMarkers depends on nowMs upstream).
  // Listing `markers` in the effect deps used to cause map.fitBounds to fire
  // every second with animate:true → visible map flicker. Keep markers in
  // a ref so fit() always reads the latest set, and trigger re-fit only on
  // mode/airport/expandedKey changes (the things that actually change which
  // bounds we want).
  const markersRef = useRef(markers);
  markersRef.current = markers;
  useEffect(() => {
    if (!map || !airport || mode === 'none') return;
    const fit = (animate) => {
      if (mode === 'airport') {
        // Airport perimeter + exactly 500 m buffer on every side.
        const BUFFER_KM = 0.5;
        const dLat = BUFFER_KM / 111;
        const dLon = BUFFER_KM / (111 * Math.cos((airport.lat * Math.PI) / 180));
        let b;
        if (airport.bounds) {
          b = L.latLngBounds(
            [airport.bounds.minLat - dLat, airport.bounds.minLon - dLon],
            [airport.bounds.maxLat + dLat, airport.bounds.maxLon + dLon],
          );
        } else {
          const HALF_KM = 1.5;
          const degLat = HALF_KM / 111;
          const degLon = HALF_KM / (111 * Math.cos((airport.lat * Math.PI) / 180));
          b = L.latLngBounds(
            [airport.lat - degLat, airport.lon - degLon],
            [airport.lat + degLat, airport.lon + degLon],
          );
        }
        map.fitBounds(b, { padding: [0, 0], animate, maxZoom: 17 });
        return;
      }
      const ms = markersRef.current || [];
      let pts = [[airport.lat, airport.lon]];
      if (mode === 'all') {
        for (const m of ms) if (m.lat != null && m.lon != null) pts.push([m.lat, m.lon]);
      } else if (mode === 'selected') {
        const sel = ms.find(m => m._sessionKey === expandedKey);
        if (sel && sel.lat != null && sel.lon != null) pts.push([sel.lat, sel.lon]);
      }
      const bounds = L.latLngBounds(pts);
      map.fitBounds(bounds, { padding: [40, 40], animate, maxZoom: 14 });
    };
    fit(true);  // initial fit can animate
    // Periodic re-fit at 15s so a marker that drifted out of view comes back —
    // but with animate:false to avoid the visible flicker that pre-fix users saw.
    const id = setInterval(() => fit(false), 15000);
    return () => clearInterval(id);
  }, [mode, airport, expandedKey, map]);
  return null;
}

/**
 * Arrivals taxiing in, from the airport's On Ground feed, as grey context markers on the
 * Departures map. Taken from AirportViewService (airport level) rather than the Live WebSocket,
 * which only carries the receiver selected on the Live page: with that receiver down the map
 * lost every arrival (Gravediggers, 2026-11-01).
 */
export function groundArrivalGhosts(onGround, departureHexes) {
  const out = [];
  for (const a of onGround || []) {
    const st = String(a.groundState || '').toLowerCase();
    if (st !== 'taxiing_in') continue;
    if (a.currentLat == null || a.currentLon == null) continue;
    const hex = (a.icaoAddress || '').toUpperCase();
    if (!hex || departureHexes.has(hex) || isGroundVehicle(a)) continue;
    out.push({
      hex,
      lat: a.currentLat,
      lon: a.currentLon,
      track: a.heading ?? 0,
      t: a.aircraftType,
      airline_icao: a.airlineIcao,
      flight: (a.callsign || hex).trim(),
    });
  }
  return out;
}

// Sort priority — most-imminent-departure first. TAKE_OFF is rolling, TAXIING
// is moving on the surface, POWER_ON is at the gate. DEPARTED and PARKED at
// the bottom (post-event / static).
const STATE_ORDER = { TAKE_OFF: 0, TAXIING: 1, POWER_ON: 2, DEPARTED: 3, PARKED: 4 };

// Airline code resolver — falls back to the first 3 chars of the callsign
// when the backend hasn't populated airlineIcao yet (the icao_airline lookup
// can lag the first ADS-B observation by several seconds). Returns null if
// the callsign isn't shaped like an ATC ICAO airline + flight number
// (3 letters then digits/letters), so we don't paint a bogus 'EI-' chip
// on a private registration like 'G-OSRL'.
function airlineFromRow(r) {
  if (r.airlineIcao) return r.airlineIcao;
  const cs = (r.callsign || '').trim();
  if (cs.length < 3) return null;
  const head = cs.slice(0, 3).toUpperCase();
  return /^[A-Z]{3}$/.test(head) ? head : null;
}

// Destination resolver — suppress "destination = our own airport". The
// LandingPredictionService callsign cache is populated with airports where
// the aircraft has been observed inbound, so for a flight that regularly
// arrives at EIDW the cache returns "EIDW" when asked, which is wrong for
// the OUTBOUND leg from EIDW.
function destForRow(r, currentAirport) {
  if (!r.destinationIata) return null;
  if (r.destinationIata.toUpperCase() === (currentAirport || '').toUpperCase()) return null;
  return r.destinationIata;
}


export default function PredictionsDepartures({ departureForecastUpdate, liveAircraft, selectedReceiverUuid, onJumpToHistory }) {
  const { mapSettings } = useSettings();
  const [selectedAirport, setSelectedAirport] = useState('EIDW');
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const { rows, loading, error } = useDepartureRows(selectedAirport, selectedDate, departureForecastUpdate);
  const [expandedKey, setExpandedKey] = useState(null);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [autoZoom, setAutoZoom] = useState('airport');

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Resizable split (mirrors arrivals)
  const [tableWidth, setTableWidth] = useState(45);
  const isResizingRef = useRef(false);
  const [isResizing, setIsResizing] = useState(false);
  const onMouseDown = e => { e.preventDefault(); isResizingRef.current = true; setIsResizing(true); };
  useEffect(() => {
    const move = e => {
      if (!isResizingRef.current) return;
      const w = ((window.innerWidth - e.clientX) / window.innerWidth) * 100;
      if (w >= 20 && w <= 70) setTableWidth(w);
    };
    const up = () => { isResizingRef.current = false; setIsResizing(false); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, []);

  const airport = useMemo(
    () => SUPPORTED_AIRPORTS.find(a => a.icao === selectedAirport) || SUPPORTED_AIRPORTS[0],
    [selectedAirport]
  );

  // Airport runways for the map overlay. Fetched once per airport, reused
  // across re-renders. Each runway has a coordinates polyline (transformed
  // to [lat, lng] pairs) plus a `ref` like "10/28" we split for end labels.
  const [runways, setRunways] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = getAccessToken();
        const resp = await getAirportFeatures(selectedAirport, token, 5);
        if (cancelled) return;
        // The feature transformer puts the runway polylines on
        // resp.features.runways with .coordinates already as [lat,lng] pairs.
        const list = (resp.features && resp.features.runways) || [];
        setRunways(list.filter(r => Array.isArray(r.coordinates) && r.coordinates.length >= 2));
      } catch {
        if (!cancelled) setRunways([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedAirport]);

  // A new airport or day closes the open forecast history.
  useEffect(() => {
    setExpandedKey(null); setHistoryEvents([]);
  }, [selectedAirport, selectedDate]);

  const handleExpand = useCallback(async (row) => {
    const key = rowKey(row.icaoAddress, row.callsign, row.sessionStartAt);
    if (expandedKey === key) { setExpandedKey(null); setHistoryEvents([]); return; }
    setExpandedKey(key); setHistoryEvents([]); setHistoryLoading(true);
    try {
      const token = getAccessToken();
      const resp = await getDepartureHistory(row.icaoAddress, selectedAirport, row.sessionStartAt, token);
      if (resp.header?.apiStatus === 'OK' || resp.header?.apiStatus === 'SUCCESS') {
        setHistoryEvents(resp.events || []);
      }
    } catch { /* ignore */ }
    finally { setHistoryLoading(false); }
  }, [expandedKey, selectedAirport]);

  // 'now' is the per-second wall clock; nowMs is its epoch ms. Declared here
  // (rather than below) because liveMarkers, visibleRows and stats all need it.
  // Putting it after a useMemo that depends on it triggers a temporal-dead-zone
  // ReferenceError at component init.
  const nowMs = now.getTime();

  // Predicted departure order (1 = next away). Declared before liveMarkers so the map's
  // sequence number and the table's # column read the same map, keyed by rowKey.
  const order = useMemo(() => departureOrder(rows, nowMs), [rows, nowMs]);

  // Live markers — overlay live WS aircraft positions onto the airport map
  // for any row in an ACTIVE state. We deliberately exclude DEPARTED and
  // PARKED here: departed aircraft are no longer on the surface (the map
  // is airport-zoomed) and parked aircraft are noise — neither belongs on
  // a "live departures" view.
  const liveMarkers = useMemo(() => {
    const out = [];
    for (const r of rows) {
      const eff = effectiveState(r, nowMs);
      if (eff !== 'POWER_ON' && eff !== 'TAXIING' && eff !== 'TAKE_OFF') continue;
      if (!r.icaoAddress) continue;
      // Drop ground vehicles defensively — these shouldn't reach the
      // departure-forecast pipeline at all but the callsign-prefix gate
      // here ensures the map never paints a DAA truck as a flight.
      if (isGroundVehicle(r)) continue;
      const hex = r.icaoAddress;
      const live = liveAircraft
        ? (liveAircraft[hex.toLowerCase()] || liveAircraft[hex.toUpperCase()] || liveAircraft[hex])
        : null;
      const lat = (live && live.lat != null) ? live.lat : r.lastLatitude;
      const lon = (live && live.lon != null) ? live.lon : r.lastLongitude;
      if (lat == null || lon == null) continue;
      const distKm = Math.hypot(lat - airport.lat, lon - airport.lon) * 111;
      if (distKm > 8) continue;  // departures view is the airport only
      const key = rowKey(r.icaoAddress, r.callsign, r.sessionStartAt);
      const flightLabel = r.callsign || r.iataFlightNumber || hex.toUpperCase();
      const ettStr = r.ettTime ? formatLocalHourMin(r.ettTime, selectedAirport) : '';
      out.push({
        ...(live || {}),
        hex, lat, lon,
        // Heading priority: live WS track -> live heading -> live true/mag
        // heading -> row's lastHeadingDegrees (carried over from previous
        // taxi-in or earlier this session). Defaults to 0 only if every
        // source is empty.
        track: live?.track ?? live?.heading ?? live?.true_heading ?? live?.mag_heading ?? r.lastHeadingDegrees ?? 0,
        // AircraftMarker reads `t` for the sprite-sheet icon position. The
        // live WS aircraft object usually carries it, but during coverage
        // gaps `live` is null and we fall back to row data — without `t`
        // explicitly set, AircraftMarker shows the default green silhouette
        // for known aircraft like a B777.
        t: live?.t ?? r.aircraftType,
        airline_icao: r.airlineIcao,
        flight: ettStr ? `${flightLabel} · ETT ${ettStr}` : flightLabel,
        // These are tracked flights with a forecast — never "new" in the
        // live-feed sense. AircraftMarker's `!last_seen_at` heuristic
        // would otherwise paint every departure green when the WS payload
        // didn't include last_seen_at (frequent for taxiing/just-pushed-back
        // aircraft).
        isNew: false,
        _sessionKey: key,
        // Same number as the table's # column, drawn by AircraftMarker as the orange badge.
        _seqNumber: order.get(key),
        _seqTitle: 'Predicted departure order',
        _isExpanded: expandedKey === key,
        _isStale: !live,
      });
    }
    return out;
  }, [rows, liveAircraft, expandedKey, airport, selectedAirport, nowMs, order]);

  // Ghost arrivals — render any non-departure aircraft inside the airport area
  // so the operator can see where shared taxiways are blocked by inbound
  // traffic. Greyed at 50% via AircraftMarker's ghostMode prop.
  //
  // Filter:
  //   1. lat/lon present in liveAircraft
  //   2. within 8 km of the airport (same window as the departure markers)
  //   3. NOT already represented as a departure marker (icao not in liveMarkers)
  //   4. on ground or low-altitude (≤ 5000 ft) — high-altitude overflights
  //      are not blocking taxiways
  //   5. NOT climbing — baro_rate > 200 fpm means the aircraft is departing,
  //      regardless of whether the FE has caught up to it as a departure row
  //      yet (departureForecast WS event is async after first surface position).
  //      Without this gate, a freshly-rotating flight gets painted as a grey
  //      ghost-arrival until its departure row finally arrives.
  // Heading falls through to track / 0; aircraft type carried over for sprite.
  // Airport-level On Ground feed (5 s), the source of taxiing-in arrivals.
  const [groundAircraft, setGroundAircraft] = useState([]);
  useEffect(() => {
    if (!selectedAirport || !selectedReceiverUuid) return undefined;
    let cancelled = false;
    const load = async () => {
      const token = getAccessToken();
      if (!token) return;
      try {
        const resp = await getAircraftOnGround(selectedAirport, selectedReceiverUuid, token);
        const ok = resp?.header?.apiStatus === 'OK' || resp?.header?.apiStatus === 'SUCCESS';
        if (!cancelled && ok) setGroundAircraft(resp.aircraft || []);
      } catch (e) {
        // Context layer only: keep the last good set rather than blank the map.
      }
    };
    load();
    const id = setInterval(load, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [selectedAirport, selectedReceiverUuid]);

  // The expanded row's aircraft: the Live page's popup and its arrival track (2 km before the
  // landing that preceded this session, to now), opened together with the row's forecast history.
  const expandedRow = useMemo(
    () => (expandedKey ? rows.find(r => rowKey(r.icaoAddress, r.callsign, r.sessionStartAt) === expandedKey) : null),
    [rows, expandedKey]);
  const landingTrack = useLandingTrack(useMemo(
    () => departureTrackQuery(expandedRow, groundAircraft), [expandedRow, groundAircraft]));

  const ghostArrivalMarkers = useMemo(() => {
    const departureHexes = new Set(liveMarkers.map(m => (m.hex || '').toUpperCase()));
    // Surface arrivals first; the Live feed below only adds aircraft still on approach.
    const out = groundArrivalGhosts(groundAircraft, departureHexes);
    const seen = new Set(out.map(m => m.hex));
    if (!liveAircraft) return out;
    let totalCandidates = 0, droppedNoPos = 0, droppedDup = 0, droppedFar = 0,
        droppedAlt = 0, droppedClimbing = 0, droppedGV = 0, kept = 0;
    for (const key of Object.keys(liveAircraft)) {
      totalCandidates++;
      const ac = liveAircraft[key];
      if (!ac || ac.lat == null || ac.lon == null) { droppedNoPos++; continue; }
      const hex = (ac.hex || key || '').toUpperCase();
      if (!hex || departureHexes.has(hex) || seen.has(hex)) { droppedDup++; continue; }
      const distKm = Math.hypot(ac.lat - airport.lat, ac.lon - airport.lon) * 111;
      if (distKm > 12) { droppedFar++; continue; }
      // Direction logic, in priority order:
      //
      //   1. flight_phase === 'DEPARTURE' (track started on the ground)
      //      → exclude. This is the project's deterministic departure
      //      rule: a new tracking session begun on the surface is by
      //      definition a departure regardless of any other signal.
      //   2. baro_rate > 200 fpm → exclude (climbing out).
      //   3. flight_phase === 'ARRIVAL' (landing detected within last
      //      hour at this airport) → INCLUDE as ghost (rollout / taxi-in).
      //   4. Fall through (no track-state direction yet — overflight,
      //      first-ever sight, or before the discontinuity gate fires):
      //      include only when airborne 200 < alt_baro <= 8000 ft.
      //
      // scheduled_direction (airlabs) is *validation only* and not used
      // as a primary signal — the source of truth is our own observed
      // track state.
      const phase = ac.flight_phase;
      if (phase === 'DEPARTURE') { droppedClimbing++; continue; }
      const baroRate = ac.baro_rate ?? ac.vert_rate ?? 0;
      if (baroRate > 200) { droppedClimbing++; continue; }
      if (phase !== 'ARRIVAL') {
        // No deterministic phase yet — fall back to airborne-on-approach.
        const alt = ac.alt_baro ?? ac.altitude;
        if (alt == null || alt <= 200) { droppedAlt++; continue; }
        if (alt > 8000) { droppedAlt++; continue; }
      }
      if (isGroundVehicle(ac)) { droppedGV++; continue; }
      kept++;
      out.push({
        ...ac,
        hex,
        lat: ac.lat,
        lon: ac.lon,
        track: ac.track ?? ac.heading ?? ac.true_heading ?? ac.mag_heading ?? 0,
        t: ac.t ?? ac.aircraftType,
        airline_icao: ac.airline_icao,
        flight: (ac.flight || ac.callsign || hex).trim(),
      });
    }
    if (typeof window !== 'undefined' && window.__GHOST_DEBUG) {
      // eslint-disable-next-line no-console
      console.debug('[ghost-arrivals]',
        { totalCandidates, kept, droppedNoPos, droppedDup, droppedFar,
          droppedAlt, droppedClimbing, droppedGV });
    }
    return out;
  }, [liveAircraft, liveMarkers, airport, groundAircraft]);

  const tileConfig = getTileLayerConfig(mapSettings.tileLayer);

  // Show parked aircraft? Off by default — they pollute the active-departures
  // view. The toggle lives next to Auto Zoom in the controls bar below.
  const [showParked, setShowParked] = useState(false);

  // Visible rows = enrich with effectiveState, sort by lifecycle order,
  // optionally hide PARKED.
  const visibleRows = useMemo(() => {
    const enriched = rows.map(r => ({ ...r, _effState: effectiveState(r, nowMs) }));
    const filtered = showParked ? enriched : enriched.filter(r => r._effState !== 'PARKED');
    filtered.sort((a, b) => {
      const so = (STATE_ORDER[a._effState] ?? 99) - (STATE_ORDER[b._effState] ?? 99);
      if (so !== 0) return so;
      // Within active states, sort by ETT ascending — first to depart at the
      // top. DEPARTED rows sort by actual liftoff DESC (most recent first).
      // PARKED falls back to last update DESC.
      if (a._effState === 'DEPARTED' || b._effState === 'DEPARTED') {
        const at = parseServerTime(a.actualLiftoffAt)?.getTime() ?? 0;
        const bt = parseServerTime(b.actualLiftoffAt)?.getTime() ?? 0;
        return bt - at;
      }
      if (a._effState === 'PARKED') {
        const at = parseServerTime(a.lastUpdateAt || a.lastForecastAt)?.getTime() ?? 0;
        const bt = parseServerTime(b.lastUpdateAt || b.lastForecastAt)?.getTime() ?? 0;
        return bt - at;
      }
      // POWER_ON / TAXIING / TAKE_OFF — ETT ascending.
      const at = parseServerTime(a.ettTime)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      const bt = parseServerTime(b.ettTime)?.getTime() ?? Number.MAX_SAFE_INTEGER;
      return at - bt;
    });
    return filtered;
  }, [rows, nowMs, showParked]);

  const stats = useMemo(() => {
    const counts = { POWER_ON: 0, TAXIING: 0, TAKE_OFF: 0, DEPARTED: 0, PARKED: 0 };
    for (const r of visibleRows) counts[r._effState] = (counts[r._effState] || 0) + 1;
    const errors = visibleRows.filter(r => r.finalErrorMinutes != null).map(r => Math.abs(r.finalErrorMinutes));
    const meanErr = errors.length ? errors.reduce((s, x) => s + x, 0) / errors.length : 0;
    // Active departure runway — mode of runway_threshold weighted by recency.
    // Two earlier bugs to avoid:
    //   1. Including ALL today's DEPARTED rows lets a busy morning of rwy 28
    //      drown out a wind-shift to rwy 10 in the afternoon (this was the
    //      visible "departures runway badge doesn't update" symptom).
    //   2. Excluding DEPARTED entirely makes the badge flicker to null
    //      between flights when nothing is currently taxiing.
    // Compromise: count POWER_ON / TAXIING / TAKE_OFF unconditionally, and
    // count DEPARTED only when the row was updated within the last 30 min.
    const rwyRecentCutoff = nowMs - 30 * 60 * 1000;
    const rwyCounts = {};
    for (const r of visibleRows) {
      if (!r.runwayThreshold) continue;
      const st = r._effState;
      if (st === 'POWER_ON' || st === 'TAXIING' || st === 'TAKE_OFF') {
        rwyCounts[r.runwayThreshold] = (rwyCounts[r.runwayThreshold] || 0) + 1;
      } else if (st === 'DEPARTED') {
        const updated = parseServerTime(r.lastUpdateAt) || parseServerTime(r.lastForecastAt);
        if (updated && updated.getTime() >= rwyRecentCutoff) {
          rwyCounts[r.runwayThreshold] = (rwyCounts[r.runwayThreshold] || 0) + 1;
        }
      }
    }
    let activeRwy = null;
    let activeRwyN = 0;
    for (const [rwy, n] of Object.entries(rwyCounts)) {
      if (n > activeRwyN) { activeRwy = rwy; activeRwyN = n; }
    }
    return { ...counts, meanErr, total: visibleRows.length, activeRwy, activeRwyN };
  }, [visibleRows, nowMs]);

  // Active / Departed groups for the table's dividers, same split as the arrivals table's
  // In Flight / Landed. STATE_ORDER already sorts every active state above DEPARTED and PARKED,
  // so the groups keep visibleRows' order.
  const groups = useMemo(() => groupDepartureRows(visibleRows, nowMs), [visibleRows, nowMs]);
  // Collapse the Departed group so aircraft still on the ground stay front-and-centre.
  const [departedCollapsed, setDepartedCollapsed] = useState(false);

  // Ground counters on the map.
  const counters = useMemo(
    () => groundCounts({ onGround: groundAircraft, departures: visibleRows, nowMs }),
    [groundAircraft, visibleRows, nowMs]
  );

  return (
    <div className="predictions-screen">
      <div className="predictions-controls">
        <div className="control-group">
          <label>Airport:</label>
          <select value={selectedAirport} onChange={e => setSelectedAirport(e.target.value)} className="airport-select">
            {SUPPORTED_AIRPORTS.map(a => <option key={a.icao} value={a.icao}>{a.name}</option>)}
          </select>
        </div>
        <div className="control-group">
          <label>Date:</label>
          <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="date-picker" max={new Date().toISOString().split('T')[0]} />
        </div>
        <div className="predictions-filters">
          <span className="filter-separator">|</span>
          <span className="airport-clock" title={`Local time at ${selectedAirport}`}>
            🕐 {formatLocalHourMin(now, selectedAirport)} {airportTzName(selectedAirport).split('/')[1] || ''}
          </span>
          <span className="filter-separator">|</span>
          <span className="filter-label">Auto Zoom:</span>
          <select className="filter-select" value={autoZoom} onChange={e => setAutoZoom(e.target.value)} title="Auto-zoom mode">
            <option value="airport">Airport (~5 km)</option>
            <option value="selected">Selected flight + airport</option>
            <option value="all">Fit all on-ground</option>
            <option value="none">Off</option>
          </select>
          <span className="filter-separator">|</span>
          <button
            className={`filter-btn${showParked ? ' active' : ''}`}
            onClick={() => setShowParked(!showParked)}
            title="Include aircraft that arrived in the last 12 h and aren't currently transmitting"
          >Show Parked</button>
          <span className="filter-separator">|</span>
          {stats.activeRwy && (
            <>
              <span className="active-runway-badge" title={`Inferred from ${stats.activeRwyN} of ${stats.total} active sessions`}>
                Active Rwy <strong>{stats.activeRwy}</strong>
              </span>
              <span className="filter-separator">|</span>
            </>
          )}
          <span className="filter-label">
            {stats.total} active  •  Power On {stats.POWER_ON}  •  Taxiing {stats.TAXIING}  •  Take-off {stats.TAKE_OFF}  •  Departed {stats.DEPARTED}
            {stats.meanErr > 0 && <>  •  MAE {stats.meanErr.toFixed(1)} min</>}
          </span>
        </div>
      </div>

      <div className="predictions-content" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <div className="predictions-map" style={{ flex: 1 }}>
          <MapContainer key={selectedAirport} center={[airport.lat, airport.lon]} zoom={14} style={{ height: '100%', width: '100%' }}>
            <TileLayer url={tileConfig.url} attribution={tileConfig.attribution} />
            <DeparturesAutoZoom mode={autoZoom} airport={airport} markers={liveMarkers} expandedKey={expandedKey} />

            {/* Runway overlay — drawn first so aircraft icons sit on top.
                The polyline is the runway centerline; threshold labels at
                each end use the two halves of the OSM `ref` (e.g. "10/28"
                → "10" at the first coordinate end, "28" at the last). The
                label markers are offset OUTWARD from the runway endpoint
                along the runway heading by ~140 m so the text sits beyond
                the threshold rather than overlapping the runway itself.
                Labels are deduped by (designator, ~50m bucket) so parallel
                runways with the same designator (e.g. two features both
                tagged "10L/28R") don't stack on top of each other. */}
            {(() => {
              // Offset a [lat,lng] point by distance (m) along bearing (deg).
              const offsetPoint = (lat, lng, distM, bearingDeg) => {
                const R = 6371000;
                const br = bearingDeg * Math.PI / 180;
                const lat1 = lat * Math.PI / 180;
                const lng1 = lng * Math.PI / 180;
                const lat2 = Math.asin(
                  Math.sin(lat1) * Math.cos(distM / R) +
                  Math.cos(lat1) * Math.sin(distM / R) * Math.cos(br)
                );
                const lng2 = lng1 + Math.atan2(
                  Math.sin(br) * Math.sin(distM / R) * Math.cos(lat1),
                  Math.cos(distM / R) - Math.sin(lat1) * Math.sin(lat2)
                );
                return [lat2 * 180 / Math.PI, lng2 * 180 / Math.PI];
              };
              // Bearing from p1 → p2 in degrees [0,360).
              const bearing = (p1, p2) => {
                const lat1 = p1[0] * Math.PI / 180;
                const lat2 = p2[0] * Math.PI / 180;
                const dLng = (p2[1] - p1[1]) * Math.PI / 180;
                const y = Math.sin(dLng) * Math.cos(lat2);
                const x = Math.cos(lat1) * Math.sin(lat2)
                        - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
                return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
              };
              const seen = new Set();  // dedupe key: `${designator}|${latbucket}|${lngbucket}`
              const dedupKey = (designator, lat, lng) => {
                // ~250m bucket so two OSM features describing the same physical
                // runway (centerline + parallel-shoulder, or two halves of the
                // same runway tagged separately) only label once. Parallel
                // runways have DIFFERENT designators (10L vs 10R), so a wide
                // bucket doesn't accidentally collapse them.
                const latB = Math.round(lat / 0.0025);
                const lngB = Math.round(lng / 0.0025);
                return `${designator}|${latB}|${lngB}`;
              };
              // Distance between two [lat,lng] pts in meters.
              const distMeters = (p1, p2) => {
                const R = 6371000;
                const lat1 = p1[0] * Math.PI / 180;
                const lat2 = p2[0] * Math.PI / 180;
                const dLat = (p2[0] - p1[0]) * Math.PI / 180;
                const dLng = (p2[1] - p1[1]) * Math.PI / 180;
                const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
                return 2 * R * Math.asin(Math.sqrt(a));
              };
              const LABEL_OFFSET_M = 140;
              const MIN_RUNWAY_LENGTH_M = 1500;  // reject short runway-shoulder/marker features
              const out = [];
              runways.forEach((rw, i) => {
                const coords = rw.coordinates;
                if (!coords || coords.length < 2) return;
                const firstEnd = coords[0];
                const secondPt = coords[1];
                const lastEnd = coords[coords.length - 1];
                const penultPt = coords[coords.length - 2];
                // Skip closed-polygon features (runway surface outline) — labels
                // computed from polygon vertices end up mid-runway. Only true
                // centerlines reach the labelling code.
                const isClosed = distMeters(firstEnd, lastEnd) < 50;
                if (isClosed) {
                  out.push(
                    <Polyline
                      key={`rwy-line-${rw.osmId || i}`}
                      positions={coords}
                      pathOptions={{ color: '#444', weight: 4, opacity: 0.4, lineCap: 'butt' }}
                    />
                  );
                  return;
                }
                // Reject features too short to be a real runway centerline —
                // marker features and partial segments end up mid-runway with
                // a duplicate designator.
                if (distMeters(firstEnd, lastEnd) < MIN_RUNWAY_LENGTH_M) {
                  return;
                }
                const refStr = (rw.ref || rw.name || '').trim();
                const halves = refStr.includes('/')
                  ? refStr.split('/').map(s => s.trim())
                  : [refStr, refStr];
                out.push(
                  <Polyline
                    key={`rwy-line-${rw.osmId || i}`}
                    positions={coords}
                    pathOptions={{ color: '#444', weight: 8, opacity: 0.6, lineCap: 'butt' }}
                  />
                );
                // First end: bearing from secondPt → firstEnd is the
                // outward direction at this threshold.
                if (halves[0]) {
                  const outBearing = bearing(secondPt, firstEnd);
                  const labelPos = offsetPoint(firstEnd[0], firstEnd[1], LABEL_OFFSET_M, outBearing);
                  const k = dedupKey(halves[0], labelPos[0], labelPos[1]);
                  if (!seen.has(k)) {
                    seen.add(k);
                    out.push(
                      <Marker
                        key={`rwy-lbl-a-${rw.osmId || i}`}
                        position={labelPos}
                        icon={L.divIcon({
                          html: `<span class="runway-label">${halves[0]}</span>`,
                          iconSize: [32, 20],
                          iconAnchor: [16, 10],
                          className: 'runway-label-icon'
                        })}
                        interactive={false}
                      />
                    );
                  }
                }
                // Last end: outward direction is bearing from penultPt → lastEnd.
                if (halves[1] && halves[1] !== halves[0]) {
                  const outBearing = bearing(penultPt, lastEnd);
                  const labelPos = offsetPoint(lastEnd[0], lastEnd[1], LABEL_OFFSET_M, outBearing);
                  const k = dedupKey(halves[1], labelPos[0], labelPos[1]);
                  if (!seen.has(k)) {
                    seen.add(k);
                    out.push(
                      <Marker
                        key={`rwy-lbl-b-${rw.osmId || i}`}
                        position={labelPos}
                        icon={L.divIcon({
                          html: `<span class="runway-label">${halves[1]}</span>`,
                          iconSize: [32, 20],
                          iconAnchor: [16, 10],
                          className: 'runway-label-icon'
                        })}
                        interactive={false}
                      />
                    );
                  }
                }
              });
              return out;
            })()}

            {/* Forecast event dots along the expanded session's history */}
            {expandedRow && historyEvents.map((ev, idx) => {
              if (ev.latitude == null || ev.longitude == null) return null;
              if (ev.eventType === 'LIFTOFF') {
                return (
                  <CircleMarker key={`hist-lift-${idx}`} center={[ev.latitude, ev.longitude]} radius={9}
                                pathOptions={{ color: cssVar('--text-primary'), fillColor: cssVar('--status-connected'), fillOpacity: 1, weight: 2.5 }}>
                    <Tooltip permanent direction="top" offset={[0, -8]}>
                      <strong>LIFTOFF {formatLocalHourMin(ev.forecastAt, selectedAirport)}</strong>
                    </Tooltip>
                  </CircleMarker>
                );
              }
              const prev = idx > 0 ? historyEvents[idx - 1] : null;
              const change = prev && prev.eventType !== 'LIFTOFF' ? ev.taxiOutMinutes - prev.taxiOutMinutes : 0;
              return (
                <CircleMarker key={`hist-fc-${idx}`} center={[ev.latitude, ev.longitude]} radius={5}
                              pathOptions={{ color: cssVar('--text-primary'), fillColor: errorToCssVar(change), fillOpacity: 0.95, weight: 1.5 }}>
                  <Tooltip>
                    <div>
                      <strong>{formatLocalHourMin(ev.forecastAt, selectedAirport)}</strong><br />
                      Predicted ETT: {ev.ettTime ? formatLocalHourMin(ev.ettTime, selectedAirport) : '-'}<br />
                      Taxi-out: {ev.taxiOutMinutes != null ? ev.taxiOutMinutes.toFixed(1) : '-'} min
                      {prev && prev.eventType !== 'LIFTOFF' && <><br />Δ vs prev: {formatDelta(change)}</>}
                    </div>
                  </Tooltip>
                </CircleMarker>
              );
            })}

            {/* Ghost arrivals — context-only inbound traffic on the same
                surface, rendered grey at 50% so departing flights stay
                primary. Drawn BEFORE the departure layer so departures
                paint on top when they overlap. */}
            {ghostArrivalMarkers.map(m => (
              <AircraftMarker
                key={`dep-ghost-${m.hex}`}
                aircraft={m}
                ghostMode={true}
                zoomLevel={14}
              />
            ))}

            {/* Live aircraft markers */}
            {liveMarkers.map(m => (
              <AircraftMarker
                key={`dep-${m._sessionKey}`}
                aircraft={m}
                isSelected={m._isExpanded}
                zoomLevel={14}
                onAircraftSelect={() => {
                  const row = rows.find(r => rowKey(r.icaoAddress, r.callsign, r.sessionStartAt) === m._sessionKey);
                  if (row) handleExpand(row);
                }}
              />
            ))}

            {/* Arrival track of the expanded row's aircraft. */}
            <LandingTrackLine positions={landingTrack.positions} />
          </MapContainer>
          <GroundCounters items={counters} />
          <LandingTrackPopup hex={expandedRow?.icaoAddress} track={landingTrack} liveAircraft={liveAircraft} />
        </div>

        <div className={`departures-resize${isResizing ? ' active' : ''}`} onMouseDown={onMouseDown} />

        <div className="predictions-table-container" style={{ width: `${tableWidth}%` }}>
          {loading && <div className="departures-empty">Loading…</div>}
          {error && <div className="departures-error">Error: {error}</div>}
          {!loading && !error && visibleRows.length === 0 && (
            <div className="departures-empty">No active departures for this airport / date.</div>
          )}
          {!loading && !error && visibleRows.length > 0 && (
            <table className="predictions-table">
              <thead>
                <tr>
                  <th className="numeric" title="Predicted departure order: aircraft on the ground numbered by predicted liftoff, 1 = next away">#</th>
                  <th>Flag</th>
                  <th title="Click hex to view this airframe in History">Hex</th>
                  <th>Reg</th>
                  <th>Airline</th>
                  <th>Callsign</th>
                  <th>Flight</th>
                  <th>Type</th>
                  <th>Dest</th>
                  <th title="Assigned gate (airlabs)">Gate</th>
                  <th title="Airline-published scheduled departure time (airlabs)">Sched</th>
                  <th title="Airline's estimated departure time (airlabs estimated_runway, refreshed every 30 min)">Airline ETD</th>
                  <th>Engines On</th>
                  <th title="Our model's estimated time of takeoff from ADS-B surface trajectory">ETT</th>
                  <th title="Estimated taxi time left: minutes until the predicted liftoff">Taxi Left</th>
                  <th title="Liftoff time predicted by the first estimate of this session">First ETT</th>
                  <th>Rwy</th>
                  <th>Status</th>
                  <th className="numeric" title="Error of the first estimate: actual liftoff minus First ETT (+ = left later)">First Δ</th>
                  <th className="numeric" title="Error of the last estimate before liftoff (+ = left later)">Final Δ</th>
                </tr>
              </thead>
              <tbody>
                {[...groups.active, ...groups.departed].map((r, idx) => {
                  const key = rowKey(r.icaoAddress, r.callsign, r.sessionStartAt);
                  const remainingMin = remainingTaxiMinutes(r, nowMs);
                  const firstErr = firstErrorMinutes(r);
                  const isExpanded = expandedKey === key;
                  const country = getCountryFromHex(r.icaoAddress);
                  // Active / Departed group dividers, as the arrivals table's In Flight / Landed.
                  const isDeparted = idx >= groups.active.length;
                  const showActiveHeader = idx === 0 && !isDeparted;
                  const showDepartedHeader = idx === groups.active.length;
                  const rowVisible = !isDeparted || !departedCollapsed;
                  return (
                    <React.Fragment key={key}>
                      {showActiveHeader && (
                        <tr className="prediction-group-divider">
                          <td colSpan={100}><span className="grp-icon">✈</span> Active <span className="grp-count">{groups.active.length}</span></td>
                        </tr>
                      )}
                      {showDepartedHeader && (
                        <tr className="prediction-group-divider departed"
                            onClick={(e) => { e.stopPropagation(); setDepartedCollapsed(v => !v); }}
                            title={departedCollapsed ? 'Show departed flights' : 'Hide departed flights'}>
                          <td colSpan={100}>
                            <span className="grp-caret">{departedCollapsed ? '▸' : '▾'}</span>{' '}
                            <span className="grp-icon">✓</span> Departed <span className="grp-count">{groups.departed.length}</span>
                          </td>
                        </tr>
                      )}
                      {rowVisible && (<>
                      <tr onClick={() => handleExpand(r)} className={isExpanded ? 'expanded' : ''}>
                        <td className="numeric">{order.get(key) ?? ''}</td>
                        <td title={r.country || ''}>
                          <FlagIcon countryCode={r.countryCode || country} country={r.country} />
                        </td>
                        <td style={{ fontFamily: 'SF Mono, Monaco, Inconsolata, Fira Mono, monospace' }}>
                          {r.icaoAddress ? (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (typeof onJumpToHistory === 'function') onJumpToHistory(r.icaoAddress);
                              }}
                              title={`View ${r.icaoAddress} in History${r.ownerOperator ? ' — ' + r.ownerOperator : ''}`}
                              style={{ color: 'var(--link-color, #4ea3ff)', textDecoration: 'none' }}
                            >
                              {r.icaoAddress}
                            </a>
                          ) : '-'}
                        </td>
                        <td title={r.aircraftDescription || ''}>{r.registration || '-'}</td>
                        <td><AirlineLogo airlineCode={airlineFromRow(r)} airlineName={airlineFromRow(r)} /></td>
                        <td>{r.callsign || '-'}</td>
                        <td style={{ fontWeight: 600 }}>{r.iataFlightNumber || '-'}</td>
                        <td>
                          {r.aircraftType && <AircraftTypeIcon aircraftType={r.aircraftType} size={14} />}{' '}
                          {r.aircraftType || '-'}
                          {r.isHeavy && (
                            <span className="heavy-badge" title="ICAO Wake Turbulence Cat H — Heavy">H</span>
                          )}
                          {r.military && (
                            <span className="heavy-badge" title="Military aircraft" style={{ background: '#8b0000' }}>M</span>
                          )}
                        </td>
                        <td>{destForRow(r, selectedAirport) || '-'}</td>
                        <td title={r.terminal ? `Terminal ${r.terminal}` : ''}>
                          {r.gate || '—'}
                          {r.gate && r.terminal && <span style={{ opacity: 0.6, fontSize: '0.85em' }}> ·T{r.terminal}</span>}
                        </td>
                        <td title={r.scheduledDeparture ? `Scheduled departure: ${formatLocalHourMin(r.scheduledDeparture, selectedAirport)}` : 'No schedule data'}>
                          {r.scheduledDeparture ? formatLocalHourMin(r.scheduledDeparture, selectedAirport) : '—'}
                        </td>
                        <td title={r.airlineEstimatedDeparture ? `Airline-reported ETD: ${formatLocalHourMin(r.airlineEstimatedDeparture, selectedAirport)}` : 'No airline ETD'}>
                          {r.airlineEstimatedDeparture ? formatLocalHourMin(r.airlineEstimatedDeparture, selectedAirport) : '—'}
                        </td>
                        <td>{formatLocalHourMin(r.sessionStartAt, selectedAirport)}</td>
                        <td>{r.actualLiftoffAt ? formatLocalHourMin(r.actualLiftoffAt, selectedAirport) : formatLocalHourMin(r.ettTime, selectedAirport)}</td>
                        <td>{remainingMin != null ? formatRemainingMmSs(remainingMin) : (r.actualLiftoffAt ? '✓' : '—')}</td>
                        <td>{r.firstEttTime ? formatLocalHourMin(r.firstEttTime, selectedAirport) : '—'}</td>
                        <td>{r.runwayThreshold || '-'}</td>
                        <td className="status-cell">
                          <GroundStateBadge state={r._effState} departure />
                        </td>
                        <td className={`numeric ${firstErr != null ? errorToBandClass(firstErr) : 'err-none'}`}>
                          {firstErr != null ? formatDelta(firstErr) : '-'}
                        </td>
                        <td className={`numeric ${r.finalErrorMinutes != null ? errorToBandClass(r.finalErrorMinutes) : 'err-none'}`}>
                          {r.finalErrorMinutes != null ? formatDelta(r.finalErrorMinutes) : '-'}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="prediction-events-row">
                          <td colSpan={100}>
                            {historyLoading && <div className="departures-empty">Loading history…</div>}
                            {!historyLoading && historyEvents.length === 0 && (
                              <div className="departures-empty">No forecast history yet.</div>
                            )}
                            {!historyLoading && historyEvents.length > 0 && (
                              <table className="predictions-table" style={{ fontSize: 12 }}>
                                <thead>
                                  <tr>
                                    <th>Forecast at</th>
                                    <th>Predicted ETT</th>
                                    <th className="numeric">Taxi-out (min)</th>
                                    <th className="numeric">Δ vs prev</th>
                                    <th>Event</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[...historyEvents].reverse().map((ev, idx, arr) => {
                                    // Newest at top. "prev" for delta means the
                                    // chronologically-earlier prediction, which
                                    // sits at idx+1 once reversed.
                                    const prevChrono = idx < arr.length - 1 ? arr[idx + 1] : null;
                                    const change = prevChrono && prevChrono.eventType !== 'LIFTOFF' && ev.eventType !== 'LIFTOFF'
                                      ? ev.taxiOutMinutes - prevChrono.taxiOutMinutes : null;
                                    return (
                                      <tr key={`ev-${idx}`}>
                                        <td>{formatLocalHourMin(ev.forecastAt, selectedAirport)}</td>
                                        <td>{ev.ettTime ? formatLocalHourMin(ev.ettTime, selectedAirport) : '-'}</td>
                                        <td className="numeric">{ev.taxiOutMinutes != null ? ev.taxiOutMinutes.toFixed(1) : '-'}</td>
                                        <td className={`numeric ${change != null ? errorToBandClass(change) : ''}`}>
                                          {change != null ? formatDelta(change) : '-'}
                                        </td>
                                        <td className={ev.eventType === 'LIFTOFF' ? 'liftoff-row' : ''}>{ev.eventType}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                      </>)}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
